// src/services/hubSync.js
//
// Cloud backup for Relationship Compass, kept in the person's Dive Deep
// Dialogues account in the Auth Hub (users/{uid}/apps/relationship-compass,
// through the hub's getAppState / saveAppState callables). Keeping it in the
// hub means deleting the DDD account deletes this backup too, with nothing
// left in Relationship Compass's own Firebase project.
//
// Design, in order of priority (after apps/mindease/src/services/HubSyncService.js):
//
//  1. LOCAL FIRST. Everything is saved on this device, always. The hub is a
//     copy, never on the path of a page load or a save.
//  2. OPT-IN. Nothing leaves the device unless the person is signed in and
//     has switched cloud backup on, on this device.
//  3. FAILS SOFT. Offline, hub down or signed out: every call returns quietly
//     and the app works exactly as it does without backup.
//  4. LAST WRITE WINS, per entry. Each entry carries the time it last changed
//     on a device. An entry this device has never synced counts as oldest, so
//     a new device takes the account's copy rather than overwriting it with
//     empty defaults. Anything a restore replaces is kept on the device in
//     relationshipCompass_replacedOnRestore.
//
// The tools write their own localStorage keys directly, so changes are found
// by fingerprinting each key (scanLocalChanges) rather than by hooking every
// save. Each entry is sent as its JSON text in chunks of up to 10,000
// characters: the hub's sanitiser cuts strings at 10,240 characters and drops
// object keys outside [a-zA-Z0-9_.-], and text chunks come back exactly as
// they were sent.
//
// Australian English throughout.

export const APP_ID = 'relationship-compass';

// Everything on this device that holds someone's work. Identity, analytics
// and partner-session keys stay on the device only.
export const CORE_KEYS = [
  'relationshipCompass_assessmentResults',
  'relationshipCompass_moduleProgress',
  'relationshipCompass_toolData',
  'relationshipCompass_toolUsage',
  'relationshipCompass_weeklyChallenges',
  'relationship-compass-gamification',
];

// The practice tools' own keys (src/components/tools). scripts/test-hub-sync.mjs
// fails if a tool starts using a key that is in neither list.
export const TOOL_KEYS = [
  'active_crisis', 'active_transition', 'affection_log', 'at_checkins',
  'conflict_history', 'daily_alignment', 'desire_assessment',
  'difficult_conversations', 'flooding_logs', 'flooding_profile', 'fw_emotions',
  'growth_goals', 'horsemen_history', 'individual_vision',
  'interdependence_score', 'intimacy_assessment', 'intimacy_logs',
  'intimacy_schedule', 'laer_attempts', 'partner_vision', 'ratio_current',
  'rc_appreciations', 'rc_deflection_pattern', 'rc_interactions',
  'rc_quiz_completed', 'rc_receiving_logs', 'relationship_rituals',
  'repair_history', 'resilience_practices', 'ritual_completions',
  'shared_vision', 'support_actions', 'transition_history', 'trust_agreements',
  'trust_compliance', 'trust_daily_actions', 'trust_gut_feeling',
  'trust_interventions', 'trust_metric', 'trust_warning_signs',
  'validation_score', 'vl_attempts', 'vulnerability_barriers',
  'vulnerability_exchanges',
];

export const SYNCED_KEYS = [...CORE_KEYS, ...TOOL_KEYS];

// Device-only bookkeeping.
export const PREF_KEY = 'relationshipCompass_cloudBackup'; // 'on' when backup is on for this device
export const META_KEY = 'relationshipCompass_backupMeta'; // { [key]: { hash, at } }
export const REPLACED_KEY = 'relationshipCompass_replacedOnRestore';

export const CHUNK_CHARS = 10000;
// The hub refuses a patch over 256KB serialised.
export const MAX_PATCH_CHARS = 240000;
const PUSH_DEBOUNCE_MS = 3000;
const SCAN_INTERVAL_MS = 20000;

// ─── Plumbing (swappable in tests) ──────────────────────────────────────────

let storage = typeof localStorage !== 'undefined' ? localStorage : null;
let now = () => Date.now();
let emulatorConnected = false;

async function defaultCall(fnName, payload) {
  const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
  const { authHubApp, authHubAuth } = await import('./firebase');
  if (!authHubApp || !authHubAuth?.currentUser) return null;
  const fns = getFunctions(authHubApp, 'australia-southeast1');
  if (import.meta.env?.VITE_USE_EMULATORS === 'true' && !emulatorConnected) {
    connectFunctionsEmulator(fns, '127.0.0.1', 5001);
    emulatorConnected = true;
  }
  const res = await httpsCallable(fns, fnName)({ appId: APP_ID, ...payload });
  return res?.data ?? null;
}
let call = defaultCall;

/** Tests only: swap storage, the hub call and the clock. */
export function __setTestHooks(hooks = {}) {
  if (hooks.storage) storage = hooks.storage;
  if (hooks.call) call = hooks.call;
  if (hooks.now) now = hooks.now;
}

// ─── Small helpers ──────────────────────────────────────────────────────────

const read = (key) => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    if (!storage) return;
    if (value === null || value === undefined) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    /* private window or full storage: the device copy stays as it was */
  }
};
const readJson = (key, fallback) => {
  try {
    const raw = read(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

// djb2, as hex. Only used to notice that a key's text changed.
export function fingerprint(text) {
  if (text === null || text === undefined) return null;
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}

// wiped: false overwrites the flag a "Delete Cloud Backup" left (saveAppState
// merges, so a field left out would survive).
export function encodeEntry(raw, at) {
  if (raw === null || raw === undefined) return { json: null, at, wiped: false };
  const json = [];
  for (let i = 0; i < raw.length; i += CHUNK_CHARS) json.push(raw.slice(i, i + CHUNK_CHARS));
  if (json.length === 0) json.push('');
  return { json, at, wiped: false };
}

export function decodeEntry(entry) {
  if (!entry || typeof entry !== 'object' || !('json' in entry)) return undefined; // not an entry
  // Emptied by "Delete Cloud Backup": the account no longer holds a copy, which
  // is not the same as the person deleting the entry. Treat it as absent, so
  // turning backup on again sends this device's copy up instead of deleting it.
  if (entry.wiped === true) return undefined;
  if (entry.json === null) return null; // deleted on some device
  return Array.isArray(entry.json) ? entry.json.join('') : undefined;
}

// Keep a patch under the hub's cap by leaving out the largest entries. They
// stay on this device and are tried again on the next push.
export function fitPatch(entries) {
  const size = (v) => {
    try {
      return JSON.stringify(v).length;
    } catch {
      return 0;
    }
  };
  const kept = { ...entries };
  let total = size({ entries: kept });
  const skipped = [];
  const byLargest = Object.keys(kept).sort((a, b) => size(kept[b]) - size(kept[a]));
  for (const key of byLargest) {
    if (total <= MAX_PATCH_CHARS) break;
    total -= size(kept[key]);
    delete kept[key];
    skipped.push(key);
  }
  return { entries: kept, skipped };
}

// ─── State ──────────────────────────────────────────────────────────────────

let signedIn = false;
let hydrated = false;
let pending = new Set();
let pushTimer = null;
let scanTimer = null;
let status = 'idle'; // idle | syncing | synced | offline | error | too-large
const listeners = new Set();

const setStatus = (next) => {
  status = next;
  listeners.forEach((fn) => {
    try {
      fn(status);
    } catch {
      /* a listener must never break sync */
    }
  });
};

export const getStatus = () => status;
export const subscribeStatus = (fn) => {
  listeners.add(fn);
  fn(status);
  return () => listeners.delete(fn);
};

export const isBackupOn = () => read(PREF_KEY) === 'on';
const isActive = () => signedIn && isBackupOn();

// ─── Change detection ───────────────────────────────────────────────────────

/** Stamp every synced key whose text changed since last seen. Returns the keys. */
export function scanLocalChanges() {
  const meta = readJson(META_KEY, {});
  const changed = [];
  for (const key of SYNCED_KEYS) {
    const hash = fingerprint(read(key));
    const seen = meta[key];
    if (!seen) {
      // First sight on this device. Unknown age, so it counts as oldest
      // (at: 0) until it changes; a key with nothing in it is simply noted.
      meta[key] = { hash, at: 0 };
      if (hash !== null) changed.push(key);
    } else if (seen.hash !== hash) {
      meta[key] = { hash, at: now() };
      changed.push(key);
    }
  }
  write(META_KEY, JSON.stringify(meta));
  return changed;
}

// ─── Hub calls ──────────────────────────────────────────────────────────────

async function hub(fnName, payload) {
  try {
    return await call(fnName, payload);
  } catch (error) {
    console.warn(`[hubSync] ${fnName} failed:`, error?.message || error);
    return null;
  }
}

/**
 * Pull the account's copy and settle each entry with this device's.
 * Remote wins when this device has no copy, or has never synced the entry,
 * or the account's copy changed more recently. Returns the restored keys.
 */
export async function hydrate() {
  if (!isActive()) return { hydrated: false, restored: [] };
  scanLocalChanges();
  setStatus('syncing');
  const res = await hub('getAppState', {});
  if (!res || res.success === false) {
    setStatus(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'error');
    return { hydrated: false, restored: [] };
  }

  const remote = res.exists && res.data && typeof res.data.entries === 'object' && res.data.entries ? res.data.entries : {};
  const meta = readJson(META_KEY, {});
  const replaced = {};
  const restored = [];

  for (const key of SYNCED_KEYS) {
    const remoteEntry = remote[key];
    const remoteText = decodeEntry(remoteEntry);
    const remoteAt = Number(remoteEntry?.at) || 0;
    const localText = read(key);
    const localAt = Number(meta[key]?.at) || 0;

    if (remoteText !== undefined && remoteAt > localAt) {
      if (remoteText !== localText) {
        if (localText !== null) replaced[key] = localText;
        write(key, remoteText);
        restored.push(key);
      }
      meta[key] = { hash: fingerprint(remoteText), at: remoteAt };
    } else if (localAt > remoteAt || (remoteText === undefined && localText !== null)) {
      pending.add(key);
    }
  }

  write(META_KEY, JSON.stringify(meta));
  if (Object.keys(replaced).length > 0) {
    write(REPLACED_KEY, JSON.stringify({ at: new Date(now()).toISOString(), entries: replaced }));
  }
  hydrated = true;
  if (restored.length > 0 && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rc:backup-restored', { detail: { keys: restored } }));
  }
  if (pending.size > 0) await flush();
  else setStatus('synced');
  return { hydrated: true, restored };
}

/** Send every pending entry now. Safe to call any time. */
export async function flush() {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  if (!isActive() || !hydrated) return { pushed: 0 };
  scanLocalChanges().forEach((key) => pending.add(key));
  if (pending.size === 0) return { pushed: 0 };

  const meta = readJson(META_KEY, {});
  const keys = [...pending];
  const entries = {};
  for (const key of keys) entries[key] = encodeEntry(read(key), Number(meta[key]?.at) || now());
  const { entries: fitted, skipped } = fitPatch(entries);
  if (Object.keys(fitted).length === 0) {
    setStatus('too-large');
    return { pushed: 0, skipped };
  }

  setStatus('syncing');
  const res = await hub('saveAppState', { patch: { entries: fitted } });
  if (!res || res.success === false) {
    setStatus(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'error');
    return { pushed: 0, skipped };
  }
  Object.keys(fitted).forEach((key) => pending.delete(key));
  setStatus(skipped.length > 0 ? 'too-large' : 'synced');
  return { pushed: Object.keys(fitted).length, skipped };
}

/** Something was saved on this device: push it shortly. */
export function noteLocalChange() {
  if (!isActive() || !hydrated) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    flush();
  }, PUSH_DEBOUNCE_MS);
}

function startWatching() {
  if (scanTimer || typeof window === 'undefined') return;
  scanTimer = setInterval(() => {
    if (scanLocalChanges().length > 0) noteLocalChange();
  }, SCAN_INTERVAL_MS);
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', onVisibility);
}
function stopWatching() {
  if (scanTimer) clearInterval(scanTimer);
  scanTimer = null;
  if (typeof window === 'undefined') return;
  window.removeEventListener('pagehide', flush);
  document.removeEventListener('visibilitychange', onVisibility);
}
function onVisibility() {
  if (document.visibilityState === 'hidden') flush();
}

// ─── Lifecycle, called by AppContext ────────────────────────────────────────

/** The hub session changed. Starts or stops sync as needed. */
export async function setSignedIn(isSignedIn) {
  signedIn = !!isSignedIn;
  if (!isActive()) {
    hydrated = false;
    stopWatching();
    setStatus('idle');
    return { hydrated: false, restored: [] };
  }
  startWatching();
  return hydrate();
}

/** Switch backup on for this device, then pull and push. */
export async function enableBackup() {
  if (!signedIn) return { ok: false, reason: 'signed-out' };
  write(PREF_KEY, 'on');
  startWatching();
  const result = await hydrate();
  return { ok: result.hydrated, restored: result.restored };
}

/** Switch backup off for this device. The account's copy is left as it is. */
export function disableBackup() {
  write(PREF_KEY, null);
  hydrated = false;
  pending = new Set();
  stopWatching();
  setStatus('idle');
}

/**
 * Empty this app's backup in the account, then switch backup off here. The
 * hub has no delete for one app's data (only a merge write), so every entry
 * is overwritten with nothing. Deleting the whole DDD account in the hub
 * removes the document itself.
 */
export async function deleteBackup() {
  if (!signedIn) return { ok: false, reason: 'signed-out' };
  const at = now();
  const entries = {};
  for (const key of SYNCED_KEYS) entries[key] = { json: null, at, wiped: true };
  const res = await hub('saveAppState', { patch: { entries, backupDeletedAt: new Date(at).toISOString() } });
  if (!res || res.success === false) return { ok: false, reason: 'hub-error' };
  disableBackup();
  return { ok: true };
}

/** Tests only. */
export function __reset() {
  stopWatching();
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = null;
  signedIn = false;
  hydrated = false;
  pending = new Set();
  status = 'idle';
}
